import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import { LicenseInfo } from '@mui/x-license';
import App from 'next/app';
import type { AppContext, AppInitialProps, AppProps as NextAppProps } from 'next/app';
import type { AppSettingsDto } from 'api/generated';
import { AppLayout } from 'components/common/app-layout';
import { ThemeProvider } from 'lib/theme-provider';
import { AppProviders } from '../lib/app-providers';
import { AppStateProvider, useAppState } from '../lib/app-state';
import { emotionCache as clientEmotionCache } from '../lib/emotion/emotion-cache';
import { fetchAppSettings } from '../lib/fetch-app-settings';
import { initPolyfills } from '../lib/polyfills';
import type { RuntimeConfig } from '../lib/runtime-config';
import { RuntimeConfigProvider, emptyRuntimeConfig, readRuntimeConfig } from '../lib/runtime-config';
import type { ApiClientHeaders } from '../lib/tanstack-query-client';
import { allLocales } from '../messages';

// Start loading polyfills immediately. Polyfills must complete before the app
// renders — AppSettingsLoader awaits them via awaitPolyfills().
// Client-only: polyfill-force mutates global Intl, which would leak across
// SSR requests. Node already ships full ICU, so server-side polyfilling is
// unnecessary anyway.
if (typeof window !== 'undefined') {
  initPolyfills(Object.keys(allLocales));
}
export type HeadersConfig = {
  appSettings: AppSettingsDto | null;
  headers: ApiClientHeaders;
};
interface AppProps extends NextAppProps {
  emotionCache?: EmotionCache;
  headersConfig: HeadersConfig;
  runtimeConfig: RuntimeConfig;
}

// Host doesn't change within a browser session, so the proxy response is
// stable across client-side navigations. Cache the first fetch and reuse it
// to avoid an extra round-trip on every page change. Server-side has no
// shared cache — each request resolves its own Origin.
let clientHeadersCache: HeadersConfig | null = null;

// Runtime config only exists on the server, so the client keeps the value it
// was rendered with and reuses it for client-side getInitialProps runs.
let clientRuntimeConfig: RuntimeConfig | null = null;
const emptyHeadersConfig: HeadersConfig = {
  appSettings: null,
  headers: {
    tenantId: null,
    skinUrl: null,
    protocol: null,
    regionalHost: null
  }
};
export default function MyApp({
  Component,
  emotionCache = clientEmotionCache,
  pageProps,
  headersConfig,
  runtimeConfig
}: AppProps) {
  LicenseInfo.setLicenseKey(process.env.X_LICENSE_PRO_KEY ?? 'fffb7317adbc6647c8aab22a26ce2e5cTz01MjI3NCxFPTE2OTcxMDgyMDc5MzcsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=');

  // Hydrate the client-side cache from server-rendered props so getInitialProps
  // short-circuits on subsequent soft navigations. Without this, client-side
  // getInitialProps would fall through to fetch('/api/appsettings-proxy'),
  // which fails with 404: trailingSlash: true redirects the URL to add a
  // trailing slash, and Pages Router API routes don't match the slash form.
  if (typeof window !== 'undefined') {
    clientHeadersCache ??= headersConfig;
    clientRuntimeConfig ??= runtimeConfig;
  }
  return <CacheProvider value={emotionCache}>
      <RuntimeConfigProvider value={runtimeConfig}>
        <AppStateProvider initialAppSettings={headersConfig.appSettings}>
          <AppProviders headersConfig={headersConfig}>
            <ThemeProvider>
              <AppContent Component={Component} pageProps={pageProps} />
            </ThemeProvider>
          </AppProviders>
        </AppStateProvider>
      </RuntimeConfigProvider>
    </CacheProvider>;
}
MyApp.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps & {
  headersConfig: HeadersConfig;
  runtimeConfig: RuntimeConfig;
}> => {
  const appProps = await App.getInitialProps(appContext);

  // Read on the server on every request so a hosting-environment change lands
  // without a rebuild; on the client, replay what the server already sent.
  const runtimeConfig = typeof window === 'undefined' ? readRuntimeConfig() : clientRuntimeConfig ?? emptyRuntimeConfig;
  if (typeof window !== 'undefined' && clientHeadersCache) {
    return {
      ...appProps,
      headersConfig: clientHeadersCache,
      runtimeConfig
    };
  }
  const {
    req
  } = appContext.ctx;
  try {
    let headersConfig: HeadersConfig;
    if (req) {
      // Server-side: call the helper directly so we don't HTTP-loopback into
      // our own /api/appsettings-proxy. With trailingSlash: true, Next.js
      // would redirect /api/appsettings-proxy -> /api/appsettings-proxy/, and
      // Pages Router API routes don't match the trailing-slash form — the
      // request would fall through to the 404 page, which would re-run this
      // getInitialProps and loop. Vercel's edge then trips a 508.
      //
      // x-forwarded-proto may be a comma-separated list when multiple proxies
      // are involved; take the first segment.
      const protoHeader = req.headers['x-forwarded-proto'];
      const protocol = (Array.isArray(protoHeader) ? protoHeader[0] : protoHeader)?.split(',')[0]?.trim() ?? 'http';
      const origin = req.headers.host ? `${protocol}://${req.headers.host}` : '';
      const result = await fetchAppSettings(origin);
      headersConfig = {
        appSettings: result.appSettings,
        headers: result.headers
      };
    } else {
      // Client-side fallback (rare — cache normally short-circuits this).
      // Goes through the proxy route so we don't hit the backend with CORS.
      const res = await fetch('/api/appsettings-proxy', {
        cache: 'no-store'
      });
      if (!res.ok) {
        throw new Error(`appsettings-proxy returned ${res.status}`);
      }
      const appSettings = (await res.json()) as AppSettingsDto;
      headersConfig = {
        appSettings,
        headers: {
          tenantId: res.headers.get('tenant-id'),
          skinUrl: res.headers.get('skin-url'),
          protocol: res.headers.get('protocol'),
          regionalHost: res.headers.get('regional-host')
        }
      };
    }
    if (typeof window !== 'undefined') {
      clientHeadersCache = headersConfig;
    }
    return {
      ...appProps,
      headersConfig,
      runtimeConfig
    };
  } catch (err) {
    // In production the backend is the source of truth — fail loudly rather
    // than render fictional default branding with a broken API baseUrl.
    if (process.env.NODE_ENV === 'production') {
      throw err;
    }
    // Dev fallback: synthesize from env so initApiClient still produces a
    // usable client.
    const parsed = process.env.NEXT_PUBLIC_API_URL ? new URL(process.env.NEXT_PUBLIC_API_URL) : null;
    console.error('[_app] falling back to env-derived headers; appsettings fetch failed:', err);
    return {
      ...appProps,
      runtimeConfig,
      headersConfig: {
        ...emptyHeadersConfig,
        headers: {
          tenantId: process.env.NEXT_PUBLIC_TENANT_ID ?? null,
          skinUrl: process.env.NEXT_PUBLIC_SKIN_URL ?? null,
          protocol: parsed?.protocol.replace(':', '') ?? null,
          regionalHost: parsed?.host ?? null
        }
      }
    };
  }
};

/** A page that renders without the navbar and mini cart, which would fire their
 *  own queries. */
type BarePage = NextAppProps['Component'] & {
  bare?: boolean;
};

/** Waits for app settings before rendering. Intl polyfills load in the background. */
function AppContent({
  Component,
  pageProps
}: {
  Component: BarePage;
  pageProps: NextAppProps['pageProps'];
}) {
  const {
    appSettingsReady
  } = useAppState();
  if (!appSettingsReady) return null;
  if (Component.bare) return <Component {...pageProps} />;
  return <AppLayout>{<Component {...pageProps} />}</AppLayout>;
}